import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { ConfirmationNumber, Payment } from "@mui/icons-material";
import { CURRENT_ACTIVATION_FEE } from "../config/membershipConfig";
import { redeemMembershipVoucher } from "../services/membership/MembershipVoucherService";

const MembershipVoucherRedeem = ({
  user,
  userProfile,
  onRedeemed,
  primaryBlue = { 700: "#1E40AF", 500: "#3B82F6", 50: "#EFF6FF" },
}) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleRedeem = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const result = await redeemMembershipVoucher({
        code,
        redeemerUid: user.uid,
        redeemerEmail: user.email,
        redeemerName: userProfile?.name,
      });
      if (!result.success) {
        setError(result.error);
        return;
      }
      setSuccess(result.message);
      setCode("");
      if (onRedeemed) onRedeemed(result);
    } catch (e) {
      setError(e.message || "Could not redeem voucher.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 4,
        border: `2px solid ${primaryBlue[500]}`,
        bgcolor: primaryBlue[50],
      }}
    >
      <CardContent>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: primaryBlue[700], mb: 1 }}
        >
          Become an Ultra member
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose one: redeem a ₹{CURRENT_ACTIVATION_FEE} membership voucher, or
          pay below and complete checkout as usual.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <ConfirmationNumber color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Option 1 — Redeem voucher
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { sm: "flex-start" },
          }}
        >
          <TextField
            fullWidth
            label="Voucher code"
            placeholder="UM-XXXXXXXX"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={loading}
          />
          <Button
            variant="contained"
            onClick={handleRedeem}
            disabled={loading || !code.trim()}
            sx={{ minWidth: { sm: 160 }, bgcolor: primaryBlue[500] }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Redeem"}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Payment color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Option 2 — Pay ₹{CURRENT_ACTIVATION_FEE}+ (packages below)
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Select a subscription and pay online — same as before.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MembershipVoucherRedeem;
